// noinspection SpellCheckingInspection

import {BaseStage} from "./BaseStage";
import {loading, stopLoading} from "../../../helpers/loading";
import Axios from "axios";
import createElement from "../../../helpers/createElement";
import Panzoom from '@panzoom/panzoom'
import {
    getAlreadyTakenCopy,
    getClearSelectionCopy,
    getCompanionPromptCopy,
    getEntityLabelsFromDataset,
    getSelectedSeatsIndicatorText,
} from "./ReservedSeatEntityLabels";
import handleAjaxErrorV3 from "../../../helpers/handleAjaxErrorV3";
import {MultimapTutorialModal} from "../Modals/MultimapTutorialModal";
import {initializeRvStallUpsell} from "../Custom/RvStallUpsell";
import {SeatMapSeatTooltip} from "./SeatMapSeatTooltip";

class ReservedSeatsSelectStage extends BaseStage {
    /** @var {number} Breakpoint for small devices */
    static BREAKPOINT_XSM = 380;
    /** @var {number} Breakpoint for small devices */
    static BREAKPOINT_SM = 576;
    /** @var {number} Breakpoint for medium devices */
    static BREAKPOINT_MD = 768;
    /** @var {number} Breakpoint for large devices */
    static BREAKPOINT_LG = 992;
    /** @var {number} Breakpoint for extra large devices */
    static BREAKPOINT_XL = 1200;

    /** @var {HTMLElement} */
    templatesContainer;

    /** @var {HTMLElement} */
    stageWrapper;

    /** @var {HTMLElement} */
    seatMapContainer;
    /** @var {HTMLElement} */
    seatMapSvgContainer;
    /** @var {HTMLElement} */
    seatMapWrapper;
    /** @var {HTMLElement|null} */
    seatMapSwitcher;
    /** @var {Function|null} */
    seatMapLayoutResizeHandler = null;
    /** @var {HTMLElement} */
    selectedSeatsIndicator;
    /** @var {HTMLElement} */
    clearSelectionButton;
    /** @var {HTMLIFrameElement} */
    legacyMapIframe;
    /** @var {import('@panzoom/panzoom').PanzoomObject} */
    panzoomInstance;

    /** @var {ReservedSeatsSelectStageDefs.SeatMapState[]} */
    mapStates = [];

    /** @var {ReservedSeatsSelectStageDefs.SeatMapState|null} */
    activeMapState = null;

    /** @var {Object} */
    seatPointerDownCache = {};

    /** @var {WeakMap<Element, SeatMapSeatTooltip>} */
    seatTooltipInstances = new WeakMap();

    /** @var {SeatMapSeatTooltip[]} */
    seatTooltipInstanceList = [];

    /** @var {WeakMap<HTMLElement, Object>} */
    multiMapSvgNavigationPointerDownCache = new WeakMap();

    animationHookActive = false;

    /** @var {ReservedSeatsSelectStageDefs.HandicapSeatDefinition[]} */
    handicapSeats;

    /** @var {import('../Custom/RvStallUpsell').RvStallUpsellDefs.Controller|null} */
    rvStallUpsellController = null;

    /**
     * @type {Object<string,Object>}
     */
    handicapSeatCompanionPopupsData = {};

    constructor(globalState) {
        super(globalState);

        // Init dom container
        this.domContainer = document.querySelector(`.stage.${this.getStageKey()}`)

        // Retrieve template container
        this.templatesContainer = document.querySelector('.templates .reserved-seats-select-stage-template');

        // Clone template
        this.stageWrapper = this.templatesContainer.querySelector('.stage-wrapper').cloneNode(true);

        this.seatMapContainer = this.stageWrapper.querySelector('.seat-map-container');
        this.seatMapWrapper = this.stageWrapper.querySelector('.seat-map-wrapper');
        this.selectedSeatsIndicator = this.stageWrapper.querySelector('.selected-seats-indicator');
        this.clearSelectionButton = this.selectedSeatsIndicator.querySelector('.clear-selection-button');

        this.clearSelectionButton.addEventListener('click', () => {
            this.clearSelection();
        })

        // Append stage to DOM
        this.domContainer.appendChild(this.stageWrapper);

        // Run base class initializations
        this.initSharedComponents();

        this.getSeatMapHtml();
    }

    getSeatMapHtml() {
        loading();

        const orderData = this.globalState.getOrderDataForServer();
        let seatMapRequestData = {
            order_id: this.globalState.orderId,
            event_date_id: orderData.event_date_id,
            sub_orders: orderData.sub_orders || [],
        };

        if (this.isMixedCompositionEnabled()) {
            seatMapRequestData = {
                ...seatMapRequestData,
                mixed_order_composition: true,
            };

            if (typeof this.globalState.getCompositionRequestDataForServer === 'function') {
                Object.assign(
                    seatMapRequestData,
                    this.globalState.getCompositionRequestDataForServer(),
                    {
                        seats: orderData.seats,
                        reserved_seats: orderData.reserved_seats,
                        addons: orderData.addons,
                        discounts: orderData.discounts,
                    }
                );
            }
        }

        Axios.post(this.globalState.selectedDate.getSeatMapRoute, seatMapRequestData)
            .then(response => {
                // A sidebar change can remove this stage while its map request is in flight.
                if (!this.stageWrapper.isConnected) {
                    return;
                }

                // Set seat map HTML
                this.seatMapContainer.innerHTML = response.data.map;
                // Initialize html
                this.initSeatMapHtml();

                if (this.isMixedCompositionEnabled()) {
                    this.restoreSelectedSeats();
                }

                stopLoading();
            })
            .catch(error => {
                if (!this.stageWrapper.isConnected) {
                    return;
                }

                const errorHandled = handleAjaxErrorV3(error);

                stopLoading()

                Promise.resolve(errorHandled).finally(() => {
                    this.globalState.setSelectedSeats([]);
                    this.globalState.stageManager.clearStageInstance('seat-select');
                    this.globalState.stageManager.goToStage('date-select');
                });
            })
    }

    /**
     * @return {PublicTicketsFormDefs.Stage}
     */
    getStageKey() {
        // noinspection JSValidateTypes
        return 'seat-select';
    }

    clearDom() {
        this.destroySeatTooltips();

        // Kill panzoom
        this.destroyPanzoomInstances();

        // Public mixed map teardown is not a user clear; the next map rebuild restores valid assignments.
        if (!this.isMixedCompositionEnabled()) {
            this.globalState.setSelectedSeats([]);
        }

        // Clear any zoom interval left over
        try {
            clearInterval(this.clearZoomGuideIntervalId);
        } catch (e) {

        }

        if (this.seatMapLayoutResizeHandler) {
            window.removeEventListener('resize', this.seatMapLayoutResizeHandler);
            this.seatMapLayoutResizeHandler = null;
        }

        // Clear stage HTML
        this.domContainer.innerHTML = '';
    }

    initSeatMapHtml() {
        this.mapStates = this.getSeatMapStates();

        if (this.mapStates.length === 0) {
            return;
        }

        this.mapStates.forEach(mapState => {
            mapState.panzoomInstance = Panzoom(
                mapState.svgContainer,
                {
                    maxScale: 4.5,
                    minScale: 0.8,
                }
            )

            // Bind zoom events
            // noinspection JSCheckFunctionSignatures
            mapState.svgContainer.addEventListener('wheel', (e, options) => this.panzoomZoomWithWheelsWrapper(e, options, mapState))
        })

        this.initSeatMapSwitcher();
        this.bindMultiMapSvgNavigationEvents();
        this.initSeatMapLayoutSizing();
        this.setupRvStallUpsell();

        this.activateMapState(this.mapStates[0]);
        this.maybeShowMultimapTutorial();


        // Bind seat selection events
        this.bindSeatTooltipEvents();
        this.bindSeatSelectionEvents();

        // Wait for scale change to hide zoom indicator
        setTimeout(() => {
            try {
                const initialZoomScale = this.getCurrentMapScale();
                this.clearZoomGuideIntervalId = setInterval(
                    () => {
                        // User has zoomed in or out, clear indicator
                        if (Math.abs(initialZoomScale - this.getCurrentMapScale()) > 0.1) {
                            this.clearZoomIndicatorGuide();
                            clearInterval(this.clearZoomGuideIntervalId);
                        }
                    },
                    2000,
                )
            } catch (e) {
                // Soemthing went wrong, remove it after 15 seconds
                setTimeout(() => {
                    this.clearZoomIndicatorGuide();
                }, 1000)

                console.error('Error setting up interval for clearing zoom guide');
                console.error(e);
            }
        }, 500);
    }

    /**
     * @return {ReservedSeatsSelectStageDefs.SeatMapState[]}
     */
    getSeatMapStates() {
        const multiMapPanels = Array.from(this.seatMapContainer.querySelectorAll('.multi-seat-map-panel'));

        if (multiMapPanels.length) {
            return multiMapPanels
                .map((panel, index) => this.createSeatMapState(panel, index))
                .filter(mapState => mapState !== null);
        }

        const standardMapWrapper = this.seatMapContainer.querySelector('.seat-map-svg-wrapper');

        if (!standardMapWrapper) {
            return [];
        }

        return [this.createSeatMapState(standardMapWrapper, 0)].filter(mapState => mapState !== null);
    }

    /**
     * @param {HTMLElement} panel
     * @param {number} index
     * @return {ReservedSeatsSelectStageDefs.SeatMapState|null}
     */
    createSeatMapState(panel, index) {
        const wrapperElement = panel.classList.contains('seat-map-svg-wrapper')
            ? panel
            : panel.querySelector('.seat-map-svg-wrapper');

        if (!(wrapperElement instanceof HTMLElement)) {
            return null;
        }

        const svgContainerElement = wrapperElement.querySelector('.seat-map-svg-container');

        if (!(svgContainerElement instanceof HTMLElement)) {
            return null;
        }

        const header = panel.querySelector('.multi-seat-map-panel-header');
        const label = (header ? header.textContent : wrapperElement.dataset.seatMapLabel || '').trim() || `Map ${index + 1}`;

        return {
            id: panel.dataset.seatMapId || wrapperElement.dataset.seatMapId || '',
            label,
            entityLabels: getEntityLabelsFromDataset(wrapperElement.dataset),
            panel,
            wrapper: wrapperElement,
            svgContainer: svgContainerElement,
            panzoomInstance: null,
            initialScaleSet: false,
            switchButton: null,
        };
    }

    initSeatMapSwitcher() {
        if (this.seatMapSwitcher) {
            this.seatMapSwitcher.remove();
            this.seatMapSwitcher = null;
        }

        if (this.mapStates.length < 2) {
            return;
        }

        const switcher = createElement(`
            <div class="multi-seat-map-selector btn-group btn-group-sm flex-wrap mb-0" role="group" aria-label="Choose seat map"></div>
        `);

        this.mapStates.forEach(mapState => {
            const button = createElement(`
                <button type="button" class="btn btn-outline-primary text-uppercase text-monospace"></button>
            `);

            button.textContent = mapState.label;
            button.addEventListener('click', () => this.activateMapState(mapState, {userTriggered: true}));

            switcher.appendChild(button);
            mapState.switchButton = button;
        })

        this.seatMapWrapper.insertBefore(switcher, this.seatMapContainer);
        this.seatMapSwitcher = switcher;
    }

    bindMultiMapSvgNavigationEvents() {
        this.mapStates.forEach(mapState => {
            mapState.svgContainer.querySelectorAll('[data-multi-seat-map-link="true"]')
                .forEach(linkElement => this.bindMultiMapSvgNavigationElement(linkElement));
        })
    }

    /**
     * @param {HTMLElement|SVGElement} linkElement
     */
    bindMultiMapSvgNavigationElement(linkElement) {
        if (linkElement.dataset.multiSeatMapLinkInitialized === 'true') {
            return;
        }

        const targetMapState = this.getMapStateById(linkElement.dataset.targetSeatMapId || '');

        if (!targetMapState) {
            this.disableMultiMapSvgNavigationElement(linkElement);
            return;
        }

        linkElement.dataset.multiSeatMapLinkInitialized = 'true';
        linkElement.addEventListener('pointerdown', event => this.handleMultiMapSvgNavigationPointerDown(event));
        linkElement.addEventListener('pointerup', event => this.handleMultiMapSvgNavigationPointerUp(event, targetMapState));
        linkElement.addEventListener('keydown', event => this.handleMultiMapSvgNavigationKeyDown(event, targetMapState));
    }

    /**
     * @param {HTMLElement|SVGElement} linkElement
     */
    disableMultiMapSvgNavigationElement(linkElement) {
        linkElement.classList.add('disabled');
        linkElement.setAttribute('aria-disabled', 'true');
        linkElement.removeAttribute('tabindex');
    }

    /**
     * @param {PointerEvent|MouseEvent|Event} event
     */
    handleMultiMapSvgNavigationPointerDown(event) {
        const linkElement = event.currentTarget;

        if (!(linkElement instanceof Element)) {
            return;
        }

        if (!this.multiMapSvgNavigationPointerDownCache) {
            this.multiMapSvgNavigationPointerDownCache = new WeakMap();
        }

        this.multiMapSvgNavigationPointerDownCache.set(linkElement, {
            x: event.screenX || 0,
            y: event.screenY || 0,
            timestamp: Date.now()
        });
    }

    /**
     * @param {PointerEvent|MouseEvent|Event} event
     * @param {ReservedSeatsSelectStageDefs.SeatMapState} targetMapState
     */
    handleMultiMapSvgNavigationPointerUp(event, targetMapState) {
        const linkElement = event.currentTarget;

        if (!(linkElement instanceof Element) || !this.multiMapSvgNavigationPointerDownCache) {
            return;
        }

        const pointerDownData = this.multiMapSvgNavigationPointerDownCache.get(linkElement);

        if (!pointerDownData) {
            return;
        }

        const currentTime = Date.now();
        const timeDiff = currentTime - pointerDownData.timestamp;
        const xDiff = Math.abs((event.screenX || 0) - pointerDownData.x);
        const yDiff = Math.abs((event.screenY || 0) - pointerDownData.y);

        // Match seat click safety so panning over an SVG link does not switch maps.
        const threshold = 5;
        const timeThreshold = 500;

        if (xDiff <= threshold && yDiff <= threshold && timeDiff <= timeThreshold) {
            this.activateMapState(targetMapState, {userTriggered: true});
        }

        this.multiMapSvgNavigationPointerDownCache.delete(linkElement);
    }

    /**
     * @param {KeyboardEvent} event
     * @param {ReservedSeatsSelectStageDefs.SeatMapState} targetMapState
     */
    handleMultiMapSvgNavigationKeyDown(event, targetMapState) {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        this.activateMapState(targetMapState, {userTriggered: true});
    }

    initSeatMapLayoutSizing() {
        if (this.seatMapLayoutResizeHandler) {
            window.removeEventListener('resize', this.seatMapLayoutResizeHandler);
            this.seatMapLayoutResizeHandler = null;
        }

        this.seatMapLayoutResizeHandler = () => this.updateSeatMapLayoutSizing();
        window.addEventListener('resize', this.seatMapLayoutResizeHandler);

        this.updateSeatMapLayoutSizing();
    }

    updateSeatMapLayoutSizing() {
        const zoomIndicator = this.stageWrapper.querySelector('.zoom-indicator');

        if (!(zoomIndicator instanceof HTMLElement)) {
            return;
        }

        const stageRect = this.domContainer.getBoundingClientRect();
        const selectedSeatsIndicatorRect = this.selectedSeatsIndicator.getBoundingClientRect();
        const switcherHeight = this.seatMapSwitcher instanceof HTMLElement
            ? Math.ceil(this.seatMapSwitcher.getBoundingClientRect().height)
            : 0;
        const availableMapHeight = Math.max(
            0,
            Math.floor(window.innerHeight - Math.max(0, stageRect.top) - selectedSeatsIndicatorRect.height - switcherHeight)
        );

        this.seatMapContainer.style.height = `${availableMapHeight}px`;
        this.seatMapContainer.style.minHeight = `${availableMapHeight}px`;

        if (!(this.seatMapSwitcher instanceof HTMLElement)) {
            zoomIndicator.style.top = '';
            return;
        }

        const wrapperRect = this.seatMapWrapper.getBoundingClientRect();
        const switcherRect = this.seatMapSwitcher.getBoundingClientRect();

        zoomIndicator.style.top = `${switcherRect.bottom - wrapperRect.top + 20}px`;
    }

    /**
     * @return {HTMLElement|null}
     */
    getMultimapTutorialWrapper() {
        const wrapper = this.seatMapContainer.querySelector('.multi-seat-map-wrapper');

        return wrapper instanceof HTMLElement ? wrapper : null;
    }

    /**
     * @param {HTMLElement|null} wrapper
     * @return {string}
     */
    getMultimapTutorialParentSeatMapId(wrapper = this.getMultimapTutorialWrapper()) {
        return wrapper instanceof HTMLElement && wrapper.dataset.seatMapId
            ? wrapper.dataset.seatMapId
            : '';
    }

    /**
     * @param {string} parentSeatMapPublicId
     * @return {string}
     */
    getMultimapTutorialSessionKey(parentSeatMapPublicId) {
        return `multimap_tutorial_seen:${parentSeatMapPublicId}`;
    }

    /**
     * @param {string} sessionKey
     * @return {boolean}
     */
    isMultimapTutorialSeen(sessionKey) {
        try {
            return window.sessionStorage.getItem(sessionKey) === '1';
        } catch (e) {
            return false;
        }
    }

    /**
     * @param {string} sessionKey
     */
    markMultimapTutorialSeen(sessionKey) {
        try {
            window.sessionStorage.setItem(sessionKey, '1');
        } catch (e) {

        }
    }

    /**
     * @param {HTMLElement|null} wrapper
     * @return {Object}
     */
    getMultimapTutorialMetadata(wrapper = this.getMultimapTutorialWrapper()) {
        if (!(wrapper instanceof HTMLElement)) {
            return {};
        }

        const input = wrapper.querySelector('input.multimap-tutorial-metadata[name="multimap_tutorial_metadata"]');

        if (!(input instanceof HTMLInputElement) || !input.value) {
            return {};
        }

        try {
            const metadata = JSON.parse(input.value);

            return metadata && typeof metadata === 'object' && Array.isArray(metadata) === false
                ? metadata
                : {};
        } catch (e) {
            return {};
        }
    }

    /**
     * @return {boolean}
     */
    shouldShowMultimapTutorial() {
        if (!this.globalState.publicTicketConfig || this.globalState.publicTicketConfig.multimapTutorialShow !== true) {
            return false;
        }

        if (this.mapStates.length < 2) {
            return false;
        }

        const wrapper = this.getMultimapTutorialWrapper();
        const parentSeatMapPublicId = this.getMultimapTutorialParentSeatMapId(wrapper);

        if (!wrapper || !parentSeatMapPublicId) {
            return false;
        }

        return this.isMultimapTutorialSeen(this.getMultimapTutorialSessionKey(parentSeatMapPublicId)) === false;
    }

    /**
     * @return {MultimapTutorialModal|null}
     */
    maybeShowMultimapTutorial() {
        if (this.shouldShowMultimapTutorial() === false) {
            return null;
        }

        const wrapper = this.getMultimapTutorialWrapper();
        const parentSeatMapPublicId = this.getMultimapTutorialParentSeatMapId(wrapper);
        const sessionKey = this.getMultimapTutorialSessionKey(parentSeatMapPublicId);
        const modal = new MultimapTutorialModal({
            metadata: this.getMultimapTutorialMetadata(wrapper),
            mapStates: this.mapStates,
            onTabRowClick: mapId => this.activateMultimapTutorialMapState(mapId),
        });

        $(modal.modalContainer).on('hidden.bs.modal', () => {
            this.markMultimapTutorialSeen(sessionKey);
        });

        return modal;
    }

    /**
     * @param {string} mapId
     */
    activateMultimapTutorialMapState(mapId) {
        const mapState = this.getMapStateById(mapId);

        if (mapState) {
            this.trackRvStallUpsellMapActivation(mapState, {userTriggered: true});
            this.activateMapState(mapState);
        }
    }

    setupRvStallUpsell() {
        if (!this.nextButton) {
            return;
        }

        if (this.rvStallUpsellController) {
            this.rvStallUpsellController.destroy();
        }

        this.rvStallUpsellController = initializeRvStallUpsell({
            globalState: this.globalState,
            getCurrentParentMapPublicId: () => this.getMultimapTutorialParentSeatMapId(this.getMultimapTutorialWrapper()),
            storage: window.sessionStorage,
            nextButton: this.nextButton,
            mapStates: this.mapStates,
            activateMapState: (mapState, activationContext) => this.activateMapState(mapState, activationContext),
            nextStage: () => this.globalState.stageManager.nextStage(),
        });
    }

    /**
     * @param {ReservedSeatsSelectStageDefs.SeatMapState} mapState
     * @param {{userTriggered?: boolean}} activationContext
     */
    trackRvStallUpsellMapActivation(mapState, activationContext = {}) {
        if (activationContext.userTriggered !== true || !this.rvStallUpsellController) {
            return;
        }

        this.rvStallUpsellController.markMapActivated(mapState, activationContext);
    }

    /**
     * @param {ReservedSeatsSelectStageDefs.SeatMapState} mapState
     * @param {{userTriggered?: boolean}} activationContext
     */
    activateMapState(mapState, activationContext = {}) {
        this.hideSeatTooltips();

        this.trackRvStallUpsellMapActivation(mapState, activationContext);

        this.activeMapState = mapState;
        this.seatMapSvgContainer = mapState.svgContainer;
        this.panzoomInstance = mapState.panzoomInstance;

        this.mapStates.forEach(candidateMapState => {
            const isActive = candidateMapState === mapState;

            candidateMapState.panel.classList.toggle('d-none', isActive === false);

            if (candidateMapState.switchButton) {
                candidateMapState.switchButton.classList.toggle('active', isActive);
            }
        })

        if (mapState.initialScaleSet === false) {
            this.setInitialScaleAndPan(mapState);
        } else {
            mapState.svgContainer.classList.remove('opacity-0');
        }

        this.updateSelectedSeatsIndicator();
    }

    destroyPanzoomInstances() {
        this.mapStates.forEach(mapState => {
            if (mapState.panzoomInstance) {
                mapState.panzoomInstance.destroy();
            }
        })

        this.panzoomInstance = null;
    }

    getCurrentViewportSize() {
        const width = window.innerWidth;
        if (width < ReservedSeatsSelectStage.BREAKPOINT_XSM) return 'xxs';
        if (width < ReservedSeatsSelectStage.BREAKPOINT_SM) return 'xs';
        if (width < ReservedSeatsSelectStage.BREAKPOINT_MD) return 'sm';
        if (width < ReservedSeatsSelectStage.BREAKPOINT_LG) return 'md';
        if (width < ReservedSeatsSelectStage.BREAKPOINT_XL) return 'lg';
        return 'xl';
    }

    setInitialScaleAndPan(mapState = this.activeMapState) {
        if (!mapState || !mapState.panzoomInstance) {
            return;
        }

        // Wait for panzoom to be ready
        setTimeout(() => {
            const viewport = this.getCurrentViewportSize();

            switch (viewport) {
                case 'xxs':
                    mapState.panzoomInstance.zoom(0.7);
                    break;
                case 'xs':
                    mapState.panzoomInstance.zoom(0.9);
                    break;
                case 'sm':
                    mapState.panzoomInstance.zoom(1.1);
                    break;
                case 'md':
                    mapState.panzoomInstance.zoom(1.3);
                    break;
                case 'lg':
                case 'xl':
                    mapState.panzoomInstance.zoom(1.50);
                    break;
            }

            const coords = this.getCenteredTransform(mapState.svgContainer.querySelector('svg'), 1, mapState);

            mapState.panzoomInstance.pan(coords.x, coords.y);

            // Make visible
            mapState.svgContainer.classList.remove('opacity-0');
            mapState.initialScaleSet = true;
        }, 256)
    }

    getCenteredTransform(element, scale = 1, mapState = this.activeMapState) {
        // TODO: This should be retrieved from the map element itself
        const xOffset = this.usesNormalizedInitialCentering(mapState) ? 0 : -25;
        const yOffset = this.usesNormalizedInitialCentering(mapState) ? 0 : 70;

        // Get the container's position and size
        const containerRect = mapState.svgContainer.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        // Calculate container center
        const containerCenterX = containerWidth / 2;
        const containerCenterY = containerHeight / 2;

        const rect = element.getBoundingClientRect();
        const elementWidth = rect.width;
        const elementHeight = rect.height;

        // Calculate element center after scaling
        const scaledElementWidth = elementWidth * scale;
        const scaledElementHeight = elementHeight * scale;
        const elementCenterX = scaledElementWidth / 2;
        const elementCenterY = scaledElementHeight / 2;

        // Calculate translation relative to container
        const translateX = containerCenterX - elementCenterX - (rect.left - containerRect.left) + xOffset;
        const translateY = containerCenterY - elementCenterY - (rect.top - containerRect.top) + yOffset;

        return {x: translateX, y: translateY};
    }

    getRenderStrategy(mapState = this.activeMapState) {
        const renderStrategyInput = mapState && mapState.wrapper
            ? mapState.wrapper.querySelector('input[name="render_strategy"]')
            : null;

        return renderStrategyInput && typeof renderStrategyInput.value === 'string'
            ? renderStrategyInput.value
            : 'legacy';
    }

    usesNormalizedInitialCentering(mapState = this.activeMapState) {
        return this.getRenderStrategy(mapState) === 'normalized_v1';
    }

    clearZoomIndicatorGuide() {
        const zoomIndicatorElement = this.stageWrapper.querySelector('.zoom-indicator');
        if (zoomIndicatorElement) {
            // First hide it for animation
            zoomIndicatorElement.style.opacity = 0;

            // Then truly it hide it
            setTimeout(() => {
                zoomIndicatorElement.classList.add('d-none');
            }, 1000)
        }
    }

    getCurrentMapScale(mapState = this.activeMapState) {
        if (!mapState) {
            return 1;
        }

        const scaleMatch = mapState.svgContainer.style.transform.match(/scale\(([+-]?\d+\.?\d*)\)/);

        return scaleMatch ? parseFloat(scaleMatch[1]) : 1;
    }

    panzoomZoomWithWheelsWrapper(event, zoomOptions, mapState = this.activeMapState) {
        return mapState.panzoomInstance.zoomWithWheel(event, zoomOptions);
    }

    /**
     * @return {Element[]}
     */
    getAvailableSeatElements(mapState = null) {
        if (mapState) {
            const availableSeats = [];

            mapState.svgContainer.querySelectorAll('.seat.available').forEach(seat => {
                availableSeats.push(seat);
            })

            return availableSeats;
        }

        return this.mapStates.reduce((availableSeats, mapState) => {
            mapState.svgContainer.querySelectorAll('.seat.available').forEach(seat => {
                availableSeats.push(seat);
            })

            return availableSeats;
        }, []);
    }

    clearSelection() {
        // Toggle all selected seats
        this.mapStates.forEach(mapState => {
            mapState.svgContainer.querySelectorAll('.seat[data-selected="true"]')
                .forEach(seat => {
                    this.toggleSeatSelection(seat);
                });
        })
    }

    bindSeatSelectionEvents() {
        const availableSeats = this.getAvailableSeatElements();

        availableSeats.forEach(seat => {
            if (seat.classList.contains('initialized')) {
                return;
            }

            seat.addEventListener('pointerdown', (event) => this.handleSeatPointerDown(event));
            seat.addEventListener('pointerup', (event) => this.handleSeatPointerUp(event));

            // Mark as initialized
            seat.classList.add('initialized');
        });
    }

    bindSeatTooltipEvents() {
        this.mapStates.forEach(mapState => {
            if (mapState.wrapper.dataset.seatTooltipShows !== 'true') {
                return;
            }

            mapState.svgContainer.querySelectorAll('.seat').forEach(seat => {
                if (this.seatTooltipInstances.has(seat)) {
                    return;
                }

                const tooltip = new SeatMapSeatTooltip({
                    mapState,
                    wrapper: mapState.wrapper,
                    seatElement: seat,
                });

                tooltip.bind();
                this.seatTooltipInstances.set(seat, tooltip);
                this.seatTooltipInstanceList.push(tooltip);
            });
        });
    }

    hideSeatTooltips() {
        this.seatTooltipInstanceList.forEach(tooltip => tooltip.hide());
    }

    destroySeatTooltips() {
        this.seatTooltipInstanceList.forEach(tooltip => tooltip.destroy());
        this.seatTooltipInstances = new WeakMap();
        this.seatTooltipInstanceList = [];
    }

    handleSeatPointerDown(event) {
        const seatKey = event.currentTarget.dataset.seatKey;
        if (!seatKey) {
            return;
        }


        this.seatPointerDownCache[seatKey] = {
            x: event.screenX,
            y: event.screenY,
            timestamp: Date.now()
        };
    }

    /**
     * @param {MouseEvent|Event} event
     */
    handleSeatPointerUp(event) {
        const seatKey = event.currentTarget.dataset.seatKey;
        if (!seatKey || !this.seatPointerDownCache[seatKey]) {
            return;
        }

        const pointerDownData = this.seatPointerDownCache[seatKey];
        const currentTime = Date.now();
        const timeDiff = currentTime - pointerDownData.timestamp;
        const xDiff = Math.abs(event.screenX - pointerDownData.x);
        const yDiff = Math.abs(event.screenY - pointerDownData.y);

        // Check if this was a "click" (within threshold and time limit)
        const threshold = 5; // 5px threshold
        const timeThreshold = 500; // 500ms threshold

        if (xDiff <= threshold && yDiff <= threshold && timeDiff <= timeThreshold) {
            // This was a tap/click, toggle selection
            if (event.altKey || event.ctrlKey || event.metaKey) {
                this.toggleRowSelection(event.currentTarget);
            } else {
                this.toggleSeatSelection(event.currentTarget);
            }
        }

        // Clean up cache entry
        delete this.seatPointerDownCache[seatKey];
    }

    /**
     * @param seatElement
     */
    toggleRowSelection(seatElement) {
        const mapState = this.getMapStateForSeatElement(seatElement);

        if (!mapState) {
            this.toggleSeatSelection(seatElement);
            return;
        }

        // First check if key matches something like [ID]_[ROW_INDEX]_[SEAT_INDEX] format
        const format = /(.*[_-])(\d+)$/;

        // Not expected format in key, just treat it as a normal seat toggle
        const match = format.exec(seatElement.dataset.seatKey);

        if (match === null) {
            this.toggleSeatSelection(seatElement);
            return;
        }

        // Split seat index from the rest of the key
        const prefix = match[1];

        // Get all seats that match this prefix
        const seatsWithSharedPrefix = Array.from(this.getAvailableSeatElements(mapState))
            .filter(seatElement => {
                const seatKey = seatElement.dataset.seatKey ?
                    seatElement.dataset.seatKey : '';

                return seatKey.startsWith(prefix);
            })

        // Toggle all matched elements, hopefully, all in the same row
        seatsWithSharedPrefix.forEach(seatElement => {
            this.toggleSeatSelection(seatElement, true);
        })

        // Trigger seat update
        this.onSeatsChange();
    }

    /**
     * @param {SVGElement} seatElement
     * @return {ReservedSeatsSelectStageDefs.SeatMapState|null}
     */
    getMapStateForSeatElement(seatElement) {
        const wrapper = seatElement.closest('.seat-map-svg-wrapper');

        if (!wrapper) {
            return null;
        }

        return this.mapStates.find(mapState => mapState.wrapper === wrapper) || null;
    }

    /**
     * @param {string} seatMapId
     * @return {ReservedSeatsSelectStageDefs.SeatMapState|null}
     */
    getMapStateById(seatMapId) {
        return this.mapStates.find(mapState => mapState.id === seatMapId) || null;
    }

    /**
     * @param {string} seatKey
     * @param {ReservedSeatsSelectStageDefs.SeatMapState|null} mapState
     * @return {SVGElement|null}
     */
    findSeatElementByKey(seatKey, mapState = null) {
        const mapStates = mapState ? [mapState] : this.mapStates;

        for (const candidateMapState of mapStates) {
            const seatElement = Array.from(candidateMapState.svgContainer.querySelectorAll('.seat'))
                .find(seatElement => seatElement.dataset.seatKey === seatKey);

            if (seatElement instanceof SVGElement) {
                return seatElement;
            }
        }

        return null;
    }

    /**
     * @param {PublicTicketsFormDefs.ReservedSeatSelection} seat
     * @return {SVGElement|null}
     */
    findSeatElementForSeat(seat) {
        if (seat.seat_map_id) {
            const mapState = this.getMapStateById(seat.seat_map_id);

            if (mapState) {
                return this.findSeatElementByKey(seat.seat_key, mapState);
            }
        }

        // Legacy selections did not carry map identity, so fall back to globally unique seat keys.
        return this.findSeatElementByKey(seat.seat_key);
    }

    /**
     * @param {SVGElement} seatElement
     * @param {string} message
     */
    markSeatAsConflict(seatElement, message = 'Seat already taken') {
        if (!(seatElement instanceof SVGElement)) {
            return;
        }

        const mapState = this.getMapStateForSeatElement(seatElement);
        const conflictMessage = message === 'Seat already taken' && mapState
            ? getAlreadyTakenCopy(mapState.entityLabels)
            : message;

        seatElement.classList.add('conflict');
        seatElement.classList.remove('available', 'selected');
        seatElement.classList.add('taken');
        seatElement.dataset.selected = 'false';

        const titleElement = seatElement.querySelector('title');
        if (titleElement && titleElement.textContent.includes(conflictMessage) === false) {
            titleElement.textContent += ` - ${conflictMessage}`;
        }
    }

    /**
     * @param {PublicTicketsFormDefs.ReservedSeatSelection[]} selectedSeats
     * @param {string} errorText
     * @return {number}
     */
    handleSeatConflicts(selectedSeats, errorText = '') {
        try {
            const previousSelectedSeats = Array.isArray(selectedSeats)
                ? selectedSeats
                : [];
            let conflicts = [];

            previousSelectedSeats.forEach(selectedSeat => {
                const seatElement = this.findSeatElementForSeat(selectedSeat);

                if (!(seatElement instanceof SVGElement)) {
                    conflicts.push({seatElement: null, mapState: null});
                    return;
                }

                const mapState = this.getMapStateForSeatElement(seatElement);
                const seatLabel = seatElement.dataset.label || '';
                const seatKey = seatElement.dataset.seatKey || selectedSeat.seat_key || '';
                const textMatchesSeat = errorText && (
                    (seatLabel && errorText.includes(seatLabel)) ||
                    (seatKey && errorText.includes(seatKey))
                );
                const looksUnavailable = seatElement.classList.contains('taken') ||
                    seatElement.classList.contains('available') === false;
                const candidate = {seatElement, mapState};

                if (textMatchesSeat || looksUnavailable) {
                    conflicts.push(candidate);
                    return;
                }

                seatElement.classList.add('selected');
                seatElement.dataset.selected = 'true';
            })

            conflicts.forEach(conflict => this.markSeatAsConflict(conflict.seatElement));

            this.onSeatsChange();
            this.globalState.stageManager.clearStageInstance('payment-info');
            this.globalState.stageManager.clearStageInstance('summary');

            const firstConflictWithMap = conflicts.find(conflict => conflict.mapState);
            if (firstConflictWithMap) {
                this.activateMapState(firstConflictWithMap.mapState);
            }

            return conflicts.length;
        } catch (error) {
            console.error('Error highlighting unavailable seats', error);
            return 0;
        }
    }

    /**
     * @param {string} errorText
     * @return {number}
     */
    highlightUnavailableSelectedSeats(errorText = '') {
        return this.handleSeatConflicts(this.globalState.selectedSeats || [], errorText);
    }


    /**
     * @param seatElement
     * @param silent Skips calling on seats change event
     */
    toggleSeatSelection(seatElement, silent = false) {
        const isSelected = seatElement.dataset.selected === 'true';

        if (isSelected) {
            seatElement.classList.remove('selected');
            seatElement.dataset.selected = 'false';
        } else {
            seatElement.classList.add('selected');
            seatElement.dataset.selected = 'true';
        }

        if (silent === false) {
            // Trigger seats change event
            this.onSeatsChange();
        }
    }

    onSeatsChange() {
        // Get seats from DOM
        /** @var {PublicTicketsFormDefs.ReservedSeatSelection[]} */
        const seats = [];

        this.mapStates.forEach(mapState => {
            mapState.svgContainer.querySelectorAll('.seat[data-selected="true"]')
                .forEach(seat => {
                    seats.push({
                        seat_key: seat.dataset.seatKey,
                        seat_group_id: seat.dataset.groupId,
                        seat_map_id: mapState.id,
                        is_handicap: seat.dataset.isHandicap === '1',
                        companion_seat_key: seat.dataset.companionSeatKey,
                    })
                });
        })

        // Check for new seat selections before updating state
        const currentSeats = this.globalState.selectedSeats || [];
        const newSeats = seats.filter(newSeat =>
            !currentSeats.some(currentSeat => currentSeat.seat_key === newSeat.seat_key)
        );

        // Handle custom behavior for new selections
        newSeats.forEach(newSeat => {
            this.onNewSeatSelection(newSeat);
        });

        // Update state
        this.globalState.setSelectedSeats(seats);

        this.updateSelectedSeatsIndicator();

        // Enable/disable next stage button
        if (
            this.globalState.selectedSeats.length
            || (
                this.isMixedCompositionEnabled()
                && this.globalState.hasGeneralAdmissionCapability()
            )
        ) {
            this.enableNextStageButton();
        } else {
            this.disableNextStageButton();
        }
    }

    restoreSelectedSeats() {
        const selectedSeats = Array.isArray(this.globalState.selectedSeats)
            ? [...this.globalState.selectedSeats]
            : [];

        selectedSeats.forEach(selectedSeat => {
            const seatElement = this.findSeatElementForSeat(selectedSeat);

            if (
                !(seatElement instanceof SVGElement)
                || seatElement.classList.contains('taken')
                || seatElement.classList.contains('available') === false
            ) {
                this.markSeatAsConflict(seatElement);
                return;
            }

            seatElement.classList.add('selected');
            seatElement.dataset.selected = 'true';
        });

        this.onSeatsChange();
    }

    updateSelectedSeatsIndicator() {
        const selectedSeats = this.globalState.selectedSeats || [];
        const seatsCount = selectedSeats.length;
        const clearSelectionCopy = getClearSelectionCopy(selectedSeats, this.mapStates, this.activeMapState);

        this.selectedSeatsIndicator.querySelector('.text').textContent =
            getSelectedSeatsIndicatorText(selectedSeats, this.mapStates, this.activeMapState);

        this.clearSelectionButton.textContent = clearSelectionCopy.label;
        this.clearSelectionButton.title = clearSelectionCopy.title;

        this.clearSelectionButton.classList.toggle('hidden', seatsCount < 1);
    }

    isMixedCompositionEnabled() {
        return typeof this.globalState.isPublicMixedCompositionEnabled === 'function'
            && this.globalState.isPublicMixedCompositionEnabled();
    }

    // noinspection JSUnusedLocalSymbols
    /**
     * Called when a new seat has been selected
     *
     * @param {PublicTicketsFormDefs.ReservedSeatSelection} selectedSeat
     */
    onNewSeatSelection(selectedSeat) {
        if (selectedSeat.is_handicap) {
            const companionSeatKey = selectedSeat.companion_seat_key;
            const mapState = this.getMapStateById(selectedSeat.seat_map_id) || this.activeMapState;

            if (companionSeatKey && mapState) {
                const companionPromptCopy = getCompanionPromptCopy(mapState.entityLabels);
                const companionSeatElement = Array.from(mapState.svgContainer.querySelectorAll('.seat:not(.taken)'))
                    .find(seat => (seat.dataset.seatKey || '').endsWith(companionSeatKey));

                if (companionSeatElement && companionSeatElement.dataset.selected !== 'true') {
                    const popupKey = `${mapState.id}:${companionSeatKey}`;

                    // Create tooltip
                    const tooltipElement = createElement(`
                            <div class="alert alert-info m-0 p-2 handicap-tooltip handicap-companion-seat-popper-tooltip">
                                <div class="tooltip-body">
                                    <div class="text">
                                        <span class="chosen-entity-label"></span><br>
                                        <span class="companion-entity-label"></span>
                                    </div>
                                    <div class="d-flex buttons">
                                        <button class="btn btn-sm btn-success yes-button flex-grow-1"><b>YES</b></button>
                                        <button class="btn btn-sm btn-light no-button ml-2 w-auto px-3">No</button>
                                    </div>
                                </div>
                                <div class="arrow" data-popper-arrow></div>
                            </div>
                        `);

                    tooltipElement.querySelector('.chosen-entity-label').textContent = companionPromptCopy.chosenText;
                    tooltipElement.querySelector('.companion-entity-label').textContent = companionPromptCopy.actionText;

                    tooltipElement.querySelector('.yes-button').addEventListener('click', () => {
                        if (companionSeatElement.dataset.selected !== 'true') {
                            this.toggleSeatSelection(companionSeatElement);
                        }
                        // And kill yourself
                        this.handicapSeatCompanionPopupsData[popupKey].element.remove();
                        this.handicapSeatCompanionPopupsData[popupKey].popperInstance.destroy();
                        delete this.handicapSeatCompanionPopupsData[popupKey];
                    })

                    tooltipElement.querySelector('.no-button').addEventListener('click', () => {
                        // kill yourself
                        this.handicapSeatCompanionPopupsData[popupKey].element.remove();
                        this.handicapSeatCompanionPopupsData[popupKey].popperInstance.destroy();
                        delete this.handicapSeatCompanionPopupsData[popupKey];
                    })

                    // Append to dom
                    mapState.wrapper.appendChild(tooltipElement);

                    // Save in memory
                    this.handicapSeatCompanionPopupsData[popupKey] = {
                        element: tooltipElement,
                        popperInstance: window.Popper.createPopper(
                            companionSeatElement,
                            tooltipElement,
                            {
                                placement: 'top',
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [0, 12],
                                        },
                                    },
                                ],
                            },
                        ),
                    }

                    this.handicapSeatCompanionPopupsData[popupKey].popperInstance.update();
                }
            }

        }
    }

    display() {
        super.display();

        if (this.animationHookActive === false) {
            // Re enable hook
            this.onAnimationFrame();
        }
    }

    /**
     * Called on each animation frame while the stage is active
     */
    onAnimationFrame() {
        this.animationHookActive = true;

        if (
            !this.domContainer || // If there is no container
            this.domContainer.innerHTML === '' || // Or has been cleared
            this.globalState.currentStage !== 'seat-select' // Or it is no longer at the seat stage
        ) {
            // Stage is gone, stop animating
            this.animationHookActive = false;
            return;
        }

        this.updateCompanionSeatTooltips();

        // Queue next frame
        requestAnimationFrame(() => {
            this.onAnimationFrame();
        })
    }


    /**
     * Updates the position of the companion seats popups that are live
     */
    updateCompanionSeatTooltips() {
        Object.values(this.handicapSeatCompanionPopupsData).forEach(popupData => {
            popupData.popperInstance.update();
        });
    }

}

export {
    ReservedSeatsSelectStage
};
